/**
 * A hover preview answers a pointer the player MOVED.
 *
 * When content appears under a resting mouse — the Collection opening over the
 * lobby tile that was just clicked, a page of cards swapped in, a card detail
 * replacing the grid — the browser hit-tests the new layout and fires a TRUSTED
 * `mouseenter` on whatever card now sits under the pointer. A catalog preview
 * is a 3.5x enlargement drawn over its neighbours, so that enter popped a huge
 * card over a screen the player had not even looked at yet, covering Next (and,
 * in the card detail, "← Back"); the pointer never moved, so nothing closed it.
 *
 * `disarm()` when a surface appears; trusted enters are then ignored until the
 * pointer really moves (a different point: an engine re-hit-testing its layout
 * reports the same one), and that first move hovers the card it is already on.
 * Synthetic enters — the touch latch's tap and the neighbour hand-off — are
 * deliberate and always admitted.
 */

/**
 * @param {{ selector: string, within?: () => (Element | null) }} options
 */
function createHoverIntent({ selector, within } = /** @type {any} */ ({})) {
    let armed = true;
    /** @type {{ x: number, y: number } | null} */
    let rest = null;
    /** @type {{ x: number, y: number } | null} */
    let last = null;

    /** @param {any} event a pointermove */
    function observeMove(event) {
        if (!event || event.isTrusted === false) return;
        if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        const point = { x: Number(event.clientX) || 0, y: Number(event.clientY) || 0 };
        last = point;
        if (armed) return;
        if (rest && Math.abs(point.x - rest.x) <= 1 && Math.abs(point.y - rest.y) <= 1) return;
        armed = true;
        const card = event.target?.closest?.(selector);
        const root = typeof within === 'function' ? within() : null;
        if (!card || (root && !root.contains(card))) return;
        card.dispatchEvent(new MouseEvent('mouseenter', { bubbles: false }));
    }

    function disarm() {
        armed = false;
        rest = last;
    }

    /** @param {any} event a mouseenter */
    function admits(event) {
        return event?.isTrusted === false || armed;
    }

    return { observeMove, disarm, admits };
}

export { createHoverIntent };
